<div class="space-y-6">
  <app-page-header
    [backTo]="['/invoices']"
    backLabel="Retour aux factures"
  >
    @if (invoice()) {
      <div slot="actions" class="flex flex-wrap items-center gap-2">
        @if (invoice()?.pdfUrl) {
          <a
            [href]="invoice()?.pdfUrl"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            Télécharger PDF
          </a>
        }
        @if (canMarkPaid()) {
          <button
            type="button"
            (click)="openPay()"
            class="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600"
          >
            Marquer payée
          </button>
        }
        @if (canCancel()) {
          <button
            type="button"
            (click)="openCancel()"
            class="inline-flex items-center justify-center gap-2 rounded-lg border border-error-300 px-4 py-2 text-sm font-medium text-error-700 transition hover:bg-error-50 dark:border-error-800 dark:text-error-400 dark:hover:bg-error-500/10"
          >
            Annuler
          </button>
        }
      </div>
    }
  </app-page-header>

  @if (loadError() || loading()) {
    <app-data-state
      [loading]="loading()"
      [error]="loadError()"
      loadingMessage="Chargement de la facture…"
      (retry)="fetch()"
    />
  } @else if (invoice(); as inv) {
    <!-- Header card -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="space-y-2">
          <div class="flex flex-wrap items-center gap-3">
            <h1 class="text-xl font-semibold text-gray-800 dark:text-white/90">{{ inv.invoiceNumber }}</h1>
            <span
              class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
              [class.bg-brand-50]="statusMeta[inv.status].palette === 'brand'"
              [class.text-brand-700]="statusMeta[inv.status].palette === 'brand'"
              [class.dark:bg-brand-500/15]="statusMeta[inv.status].palette === 'brand'"
              [class.dark:text-brand-400]="statusMeta[inv.status].palette === 'brand'"
              [class.bg-blue-light-50]="statusMeta[inv.status].palette === 'blue-light'"
              [class.text-blue-light-700]="statusMeta[inv.status].palette === 'blue-light'"
              [class.dark:bg-blue-light-500/15]="statusMeta[inv.status].palette === 'blue-light'"
              [class.dark:text-blue-light-400]="statusMeta[inv.status].palette === 'blue-light'"
              [class.bg-warning-50]="statusMeta[inv.status].palette === 'warning'"
              [class.text-warning-700]="statusMeta[inv.status].palette === 'warning'"
              [class.dark:bg-warning-500/15]="statusMeta[inv.status].palette === 'warning'"
              [class.dark:text-warning-400]="statusMeta[inv.status].palette === 'warning'"
              [class.bg-gray-100]="statusMeta[inv.status].palette === 'gray'"
              [class.text-gray-700]="statusMeta[inv.status].palette === 'gray'"
              [class.dark:bg-gray-800]="statusMeta[inv.status].palette === 'gray'"
              [class.dark:text-gray-300]="statusMeta[inv.status].palette === 'gray'"
              [class.bg-error-50]="statusMeta[inv.status].palette === 'error'"
              [class.text-error-700]="statusMeta[inv.status].palette === 'error'"
              [class.dark:bg-error-500/15]="statusMeta[inv.status].palette === 'error'"
              [class.dark:text-error-400]="statusMeta[inv.status].palette === 'error'"
            >
              {{ statusMeta[inv.status].label }}
            </span>
            @if (isOverdue() && inv.status !== 'OVERDUE') {
              <span class="inline-flex items-center gap-1 rounded-full bg-error-50 px-2 py-0.5 text-xs font-medium text-error-700 dark:bg-error-500/10 dark:text-error-400">
                Échéance dépassée
              </span>
            }
          </div>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            @if (inv.rental; as r) {
              <a [routerLink]="['/rentals', r.id]" class="text-brand-600 hover:text-brand-700 dark:text-brand-400">
                Location #{{ r.id }}
              </a>
              @if (r.vehicle?.licencePlate) {
                <span class="mx-1.5 text-gray-300 dark:text-gray-600">·</span>
                <span class="font-mono">{{ r.vehicle?.licencePlate }}</span>
              }
            } @else {
              <span class="text-gray-400">Location non renseignée</span>
            }
          </p>
        </div>
        <div class="text-right">
          <p class="text-xs text-gray-500 dark:text-gray-400">Total à payer</p>
          <p class="text-2xl font-bold text-gray-800 dark:text-white/90">
            {{ (inv.totalAmount ?? 0) | number:'1.0-0' }} <span class="text-sm font-normal text-gray-400">XOF</span>
          </p>
        </div>
      </div>
    </section>

    <div class="grid gap-6 lg:grid-cols-3">
      <!-- Dates -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Dates</h2>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Émise le</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ inv.issueDate | ldate:'dd/MM/yyyy' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Échéance</dt>
            <dd class="text-right" [class.text-error-700]="isOverdue()" [class.dark:text-error-400]="isOverdue()" [class.text-gray-800]="!isOverdue()" [class.dark:text-white/90]="!isOverdue()">
              {{ inv.dueDate | ldate:'dd/MM/yyyy' }}
            </dd>
          </div>
          @if (inv.paidDate) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">Payée le</dt>
              <dd class="text-right text-brand-700 dark:text-brand-400">{{ inv.paidDate | ldate:'dd/MM/yyyy HH:mm' }}</dd>
            </div>
          }
        </dl>
      </section>

      <!-- Parties -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Parties</h2>
        <div class="mt-4 space-y-4">
          <div>
            <p class="mb-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Locataire</p>
            <app-user-cell [user]="inv.renterUser" [fallbackId]="inv.renterId" size="md" />
          </div>
          <div class="border-t border-gray-100 pt-4 dark:border-gray-800">
            <p class="mb-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Propriétaire</p>
            <app-user-cell [user]="inv.ownerUser" [fallbackId]="inv.ownerId" size="md" />
          </div>
        </div>
      </section>

      <!-- Paiement -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Paiement</h2>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Méthode</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ inv.paymentMethod || '—' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Référence</dt>
            <dd class="text-right font-mono text-xs text-gray-700 dark:text-gray-300 break-all">{{ inv.paymentReference || '—' }}</dd>
          </div>
        </dl>
      </section>
    </div>

    <!-- Détail financier -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Détail du calcul</h2>
      <dl class="mt-4 grid gap-2 text-sm">
        <div class="flex justify-between gap-4 py-1.5">
          <dt class="text-gray-500 dark:text-gray-400">Montant location</dt>
          <dd class="text-right text-gray-800 dark:text-white/90">{{ (inv.rentalAmount ?? 0) | number:'1.0-0' }} XOF</dd>
        </div>
        @if ((inv.lateFee ?? 0) > 0) {
          <div class="flex justify-between gap-4 py-1.5">
            <dt class="text-gray-500 dark:text-gray-400">Frais de retard</dt>
            <dd class="text-right text-error-700 dark:text-error-400">+ {{ (inv.lateFee ?? 0) | number:'1.0-0' }} XOF</dd>
          </div>
        }
        @if ((inv.additionalCharges ?? 0) > 0) {
          <div class="flex justify-between gap-4 py-1.5">
            <dt class="text-gray-500 dark:text-gray-400">Frais additionnels</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">+ {{ (inv.additionalCharges ?? 0) | number:'1.0-0' }} XOF</dd>
          </div>
        }
        @if ((inv.discount ?? 0) > 0) {
          <div class="flex justify-between gap-4 py-1.5">
            <dt class="text-gray-500 dark:text-gray-400">Remise</dt>
            <dd class="text-right text-brand-700 dark:text-brand-400">− {{ (inv.discount ?? 0) | number:'1.0-0' }} XOF</dd>
          </div>
        }
        <div class="flex justify-between gap-4 border-t border-gray-200 pt-2 dark:border-gray-800">
          <dt class="font-medium text-gray-700 dark:text-gray-300">Sous-total</dt>
          <dd class="text-right font-medium text-gray-800 dark:text-white/90">{{ (inv.subtotal ?? 0) | number:'1.0-0' }} XOF</dd>
        </div>
        @if ((inv.taxAmount ?? 0) > 0) {
          <div class="flex justify-between gap-4 py-1.5">
            <dt class="text-gray-500 dark:text-gray-400">Taxes</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">+ {{ (inv.taxAmount ?? 0) | number:'1.0-0' }} XOF</dd>
          </div>
        }
        <div class="flex justify-between gap-4 border-t border-gray-200 pt-2 dark:border-gray-800">
          <dt class="text-base font-semibold text-gray-800 dark:text-white/90">Total</dt>
          <dd class="text-right text-base font-semibold text-gray-800 dark:text-white/90">{{ (inv.totalAmount ?? 0) | number:'1.0-0' }} XOF</dd>
        </div>
      </dl>
    </section>

    <!-- Transactions liées (lazy-loaded depuis payment-service) -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex items-center justify-between">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Transactions liées</h2>
        @if (linkedTxs().length > 0) {
          <a routerLink="/transactions" class="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400">
            Voir toutes les transactions
          </a>
        }
      </div>
      @if (linkedTxsLoading()) {
        <p class="mt-3 text-xs text-gray-400">Chargement…</p>
      } @else if (linkedTxs().length === 0) {
        <p class="mt-3 text-sm text-gray-400">Aucune tentative de paiement enregistrée pour cette facture.</p>
      } @else {
        <ul class="mt-4 divide-y divide-gray-100 dark:divide-gray-800">
          @for (t of linkedTxs(); track t.id) {
            <li class="flex items-center justify-between gap-4 py-3">
              <div class="min-w-0">
                <a [routerLink]="['/transactions', t.id]" class="block truncate text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400">
                  {{ t.idFromClient }}
                </a>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ t.paymentMethod?.name || t.serviceCode }} · {{ t.createdAt | ldate:'dd/MM/yy HH:mm' }}
                </p>
              </div>
              <div class="flex items-center gap-3">
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                  [class.bg-brand-50]="txStatusMeta[t.transactionStatus].palette === 'brand'"
                  [class.text-brand-700]="txStatusMeta[t.transactionStatus].palette === 'brand'"
                  [class.bg-warning-50]="txStatusMeta[t.transactionStatus].palette === 'warning'"
                  [class.text-warning-700]="txStatusMeta[t.transactionStatus].palette === 'warning'"
                  [class.bg-gray-100]="txStatusMeta[t.transactionStatus].palette === 'gray'"
                  [class.text-gray-700]="txStatusMeta[t.transactionStatus].palette === 'gray'"
                  [class.bg-error-50]="txStatusMeta[t.transactionStatus].palette === 'error'"
                  [class.text-error-700]="txStatusMeta[t.transactionStatus].palette === 'error'"
                  [class.bg-blue-light-50]="txStatusMeta[t.transactionStatus].palette === 'blue-light'"
                  [class.text-blue-light-700]="txStatusMeta[t.transactionStatus].palette === 'blue-light'"
                >
                  {{ txStatusMeta[t.transactionStatus].label }}
                </span>
                <span class="whitespace-nowrap text-sm font-medium text-gray-800 dark:text-white/90">
                  {{ t.amount | number:'1.0-0' }} <span class="text-xs font-normal text-gray-400">{{ t.currency }}</span>
                </span>
              </div>
            </li>
          }
        </ul>
      }
    </section>

    @if (inv.notes) {
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Notes</h2>
        <p class="mt-3 whitespace-pre-line text-sm text-gray-700 dark:text-gray-300">{{ inv.notes }}</p>
      </section>
    }

    <!-- Métadonnées -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Métadonnées</h2>
      <dl class="mt-4 grid gap-3 text-sm sm:grid-cols-2">
        <div class="flex justify-between gap-4">
          <dt class="text-gray-500 dark:text-gray-400">Créée le</dt>
          <dd class="text-right text-gray-800 dark:text-white/90">{{ inv.createdAt | ldate:'dd/MM/yyyy HH:mm' }}</dd>
        </div>
        @if (inv.updatedAt) {
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Mise à jour</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ inv.updatedAt | ldate:'dd/MM/yyyy HH:mm' }}</dd>
          </div>
        }
      </dl>
    </section>
  }
</div>

<!-- Mark as paid modal -->
<app-modal [isOpen]="payOpen()" (close)="closePay()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Marquer la facture payée</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    {{ invoice()?.invoiceNumber }} — {{ (invoice()?.totalAmount ?? 0) | number:'1.0-0' }} XOF
  </p>

  @if (payError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ payError() }}
    </div>
  }

  <form (ngSubmit)="submitPay()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Méthode de paiement</label>
      <app-pretty-select
        [formControl]="paymentMethodCtrl"
        [options]="paymentMethodOptions"
        placeholder="Choisir une méthode"
      />
    </div>
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
        Référence (optionnelle)
      </label>
      <input
        type="text"
        [formControl]="paymentReferenceCtrl"
        placeholder="ID transaction, numéro de reçu…"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      />
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button
        type="button"
        (click)="closePay()"
        [disabled]="paying()"
        class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
      >
        Annuler
      </button>
      <button
        type="submit"
        [disabled]="paying() || paymentMethodCtrl.invalid"
        class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        @if (paying()) { Enregistrement… } @else { Confirmer }
      </button>
    </div>
  </form>
</app-modal>

<!-- Cancel modal -->
<app-modal [isOpen]="cancelOpen()" (close)="closeCancel()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Annuler la facture</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    {{ invoice()?.invoiceNumber }} sera marquée comme annulée. Cette action peut être réversible côté back-end.
  </p>

  @if (cancelError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ cancelError() }}
    </div>
  }

  <form (ngSubmit)="submitCancel()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
        Raison (optionnelle)
      </label>
      <textarea
        rows="3"
        [formControl]="cancelReasonCtrl"
        placeholder="Ex. location annulée, doublon de facturation…"
        class="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      ></textarea>
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button
        type="button"
        (click)="closeCancel()"
        [disabled]="cancelling()"
        class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
      >
        Retour
      </button>
      <button
        type="submit"
        [disabled]="cancelling()"
        class="rounded-lg bg-error-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-error-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        @if (cancelling()) { Annulation… } @else { Confirmer l'annulation }
      </button>
    </div>
  </form>
</app-modal>
